import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleAlert, CircleCheck, Info, Loader2, Lock, WifiOff } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import { PasswordInput } from '@/src/renderer/components/ui/PasswordInput'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import { useBackupBridge, type MainSyncResult, type OpenedBackup } from '@/src/renderer/lib/backup/backupBridge'
import { isEmptySnapshot, type BackupTables } from '@/src/renderer/lib/backup/format'
import { allSelectableKeys, buildSelectionTree, selectRows, selectsEverything, withoutKey } from '@/src/renderer/lib/backup/selection'
import {
  BackupFileError, readProtectedBackup, unpackBackup, unpackDecrypted,
  type BackupErrorCode, type ProtectedBackup, type UnpackedBackup,
} from '@/src/renderer/lib/backup/unpackBackup'
import { localIdsToLookUp, planRestore, type PlanRestoreInput } from '@/src/renderer/lib/backup/planRestore'
import { buildRestorePreview, summarize, SUMMARY_KINDS, type SummaryKind } from '@/src/renderer/lib/backup/restorePreview'
import { SettingsSubpage } from '../../primitives'
import { BackupSelectionTree } from './BackupSelectionTree'
import { RestorePreview, useSummaryText } from './RestorePreview'
import './BackupViews.scss'

interface RestoreBackupViewProps {
  file: OpenedBackup
  onBack: () => void
}

/** Das Gerät, wie es vor dem Wiederherstellen dasteht — mit Sync erst nach dem Holen. */
interface DeviceState {
  device: BackupTables
  accountId: string | null
  /** Geräte-Sync ist an und hat eben geholt: Cloud-Kennungen der Datei bleiben. */
  synced: boolean
  /** Mit Geräte-Sync, aber ohne Verbindung — Wiederherstellen gesperrt (Entscheidung 20). */
  needsConnection: boolean
  /** Eigene Cloud-Zeilen zu Kennungen, die nur die Datei kennt (`localIdsToLookUp`). */
  cloudRows: PlanRestoreInput['cloudRows']
}

type Run =
  | { state: 'idle' }
  | { state: 'running' }
  | { state: 'done'; revived: Record<SummaryKind, number>; added: Record<SummaryKind, number>; skipped: number }
  | { state: 'failed' }

const sum = (counts: Record<string, number>) => SUMMARY_KINDS.reduce((total, kind) => total + (counts[kind] ?? 0), 0)

/** Wie weit die Datei offen ist: lesbar, kaputt, wird entsperrt, wartet auf das Passwort. */
type Opened =
  | { state: 'ready'; backup: UnpackedBackup }
  | { state: 'failed'; code: Exclude<BackupErrorCode, 'encrypted'> }
  | { state: 'unlocking' }
  | { state: 'locked'; sealed: ProtectedBackup; wrong: boolean; checking: boolean }

function openFile(file: OpenedBackup): Opened {
  try {
    return { state: 'ready', backup: unpackBackup(file.bytes) }
  } catch (error) {
    const code = error instanceof BackupFileError ? error.code : 'invalid'
    return code === 'encrypted' ? { state: 'unlocking' } : { state: 'failed', code }
  }
}

/**
 * **Backups › Backup wiederherstellen** (#158, ADR 0035): Kopf mit Datum, Gerät und App-Version, der
 * Baum aus der Datei, darüber die Vorschau — neu gerechnet bei jedem Haken. Vorhandenes bleibt,
 * Fehlendes und Gelöschtes kommt zurück.
 *
 * Ein geschütztes Backup (Entscheidung 17) öffnet der Hauptprozess erst still mit den Passwörtern
 * dieses Geräts; passt keins, fragt die Unterseite. Falsch → „Falsches Passwort“.
 *
 * Mit Geräte-Sync holt das Hauptfenster vorher den Stand der Cloud; ohne Verbindung ist der Button
 * gesperrt, die Vorschau bleibt sichtbar.
 */
export function RestoreBackupView({ file, onBack }: RestoreBackupViewProps) {
  const { t, i18n } = useTranslation()
  const bridge = useBackupBridge()
  const [opened, setOpened] = useState<Opened>(() => openFile(file))
  const [password, setPassword] = useState('')

  const unlock = async (sealed: ProtectedBackup, given?: string): Promise<Opened> => {
    const result = await bridge.decrypt(sealed.data, sealed.manifest.crypto, given)
      .catch((): Awaited<ReturnType<typeof bridge.decrypt>> => ({ ok: false, reason: 'invalid' }))
    if (!result.ok) {
      return result.reason === 'invalid' ? { state: 'failed', code: 'invalid' } : { state: 'locked', sealed, wrong: given !== undefined, checking: false }
    }
    try {
      return { state: 'ready', backup: unpackDecrypted(sealed.manifest, result.bytes) }
    } catch {
      return { state: 'failed', code: 'invalid' }
    }
  }

  useEffect(() => {
    if (opened.state !== 'unlocking') return
    let cancelled = false
    let sealed: ProtectedBackup
    try {
      sealed = readProtectedBackup(file.bytes)
    } catch {
      setOpened({ state: 'failed', code: 'invalid' })
      return
    }
    void unlock(sealed).then(next => { if (!cancelled) setOpened(next) })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- einmal je Datei
  }, [opened.state, file])

  const onUnlock = async (event: FormEvent) => {
    event.preventDefault()
    if (opened.state !== 'locked' || !password || opened.checking) return
    setOpened({ ...opened, checking: true })
    setOpened(await unlock(opened.sealed, password))
  }

  if (opened.state === 'ready') return <RestoreBackupContent backup={opened.backup} onBack={onBack} />

  const title = t('preferences.backups.restoreTitle')
  const pageTitle = t('settings.subNav.backups')

  return (
    <SettingsSubpage pageTitle={pageTitle} title={title} onBack={onBack}>
      {opened.state === 'failed' && (
        <p className="backup-views__result backup-views__result--failed" role="alert">
          <CircleAlert size={16} aria-hidden="true" />
          {t(opened.code === 'newer' ? 'preferences.backups.restoreView.errorNewer' : 'preferences.backups.restoreView.errorInvalid')}
        </p>
      )}
      {opened.state === 'unlocking' && (
        <p className="backup-views__loading">
          <Loader2 size={16} className="backup-views__spinner" aria-hidden="true" />
          {t('preferences.backups.restoreView.preparing')}
        </p>
      )}
      {opened.state === 'locked' && (
        <>
          <SourceLine manifest={opened.sealed.manifest} language={i18n?.language} />
          <p className="backup-views__result">
            <Lock size={16} aria-hidden="true" />
            {t('preferences.backups.password.ask')}
          </p>
          <form className="backup-views__unlock" onSubmit={event => void onUnlock(event)}>
            <label className="backup-views__label" htmlFor="restore-backup-password">{t('preferences.backups.password.password')}</label>
            <div className="backup-views__unlock-row">
              <PasswordInput
                id="restore-backup-password"
                value={password}
                onChange={event => setPassword(event.target.value)}
                autoComplete="off"
                autoFocus
                error={opened.wrong}
              />
              <Button
                type="submit"
                variant="primary"
                disabled={!password || opened.checking}
                icon={opened.checking ? <Loader2 size={16} className="backup-views__spinner" aria-hidden="true" /> : undefined}
              >
                {t('preferences.backups.password.unlock')}
              </Button>
            </div>
            <div role="status" aria-live="polite">
              {opened.wrong && !opened.checking && (
                <p className="backup-views__result backup-views__result--failed">
                  <CircleAlert size={16} aria-hidden="true" />
                  {t('preferences.backups.password.wrong')}
                </p>
              )}
            </div>
          </form>
        </>
      )}
    </SettingsSubpage>
  )
}

/** Herkunft: Datum, Gerät, App-Version — auch ein geschütztes Backup verrät das. */
function SourceLine({ manifest, language }: { manifest: UnpackedBackup['manifest']; language?: string }) {
  const { t } = useTranslation()
  const exportedAt = new Date(manifest.exportedAt)
  return (
    <p className="backup-views__source">
      {t('preferences.backups.restoreView.source', {
        date: Number.isNaN(exportedAt.getTime()) ? '—' : exportedAt.toLocaleString(language, { dateStyle: 'medium', timeStyle: 'short' }),
        device: manifest.deviceName || '—',
        version: manifest.appVersion || '—',
      })}
    </p>
  )
}

/** Die offene Datei: Baum, Vorschau, Wiederherstellen. */
function RestoreBackupContent({ backup, onBack }: { backup: UnpackedBackup; onBack: () => void }) {
  const { t, i18n } = useTranslation()
  const dataSource = useDataSource()
  const account = useAccountSource()
  const bridge = useBackupBridge()

  const tree = useMemo(() => buildSelectionTree(backup.data.tables), [backup])
  const [state, setState] = useState<DeviceState | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [run, setRun] = useState<Run>({ state: 'idle' })

  /**
   * Holt (mit Sync) und liest das Gerät. Mit Sync schlägt es dazu in der Cloud nach, was die Datei nur
   * unter ihrer lokalen Kennung kennt — auf diesem Gerät kann dieselbe Zeile anders heißen.
   */
  const loadDevice = async (): Promise<DeviceState> => {
    const accountId = await account.getUserId().catch(() => null)
    const sync: MainSyncResult = accountId
      ? await bridge.syncInMain().catch((): MainSyncResult => ({ ok: false, reason: 'failed' }))
      : { ok: true, synced: false }
    const { tables } = await dataSource.readBackupRows({ includeDeleted: true, accountId })
    const synced = sync.ok && sync.synced
    const own = backup.data.accountId === null || backup.data.accountId === accountId
    let cloudRows: DeviceState['cloudRows'] = {}
    let lookupFailed = false
    if (synced && own) {
      for (const [table, ids] of Object.entries(localIdsToLookUp(backup.data.tables, tables))) {
        const found = await account.findCloudRowsByLocalIds(table, ids).catch(() => null)
        if (!found) lookupFailed = true
        else cloudRows = { ...cloudRows, [table]: found }
      }
    }
    return { device: tables, accountId, synced, needsConnection: !sync.ok || lookupFailed, cloudRows }
  }

  useEffect(() => {
    let cancelled = false
    void loadDevice().then(next => {
      if (cancelled) return
      setState(next)
      // Einstellungen: aus, außer HappyTask ist leer (frisch installiert, Entscheidung 2a).
      const all = allSelectableKeys(tree)
      setSelected(isEmptySnapshot(next.device) ? all : withoutKey(all, 'settings'))
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- einmal je Datei
  }, [backup])

  const foreignAccount = !!state && backup.data.accountId !== null && backup.data.accountId !== state.accountId

  const plan = useMemo(() => {
    if (!state) return null
    return planRestore({
      file: selectRows(backup.data.tables, selected, { allTags: selectsEverything(tree, selected) }),
      device: state.device,
      foreignAccount,
      accountId: state.accountId,
      keepCloudIds: state.synced && !foreignAccount,
      cloudRows: state.cloudRows,
    })
  }, [backup, state, selected, tree, foreignAccount])
  const preview = useMemo(() => (plan && state ? buildRestorePreview(plan, state.device) : null), [plan, state])

  const onRestore = async () => {
    if (!state || run.state === 'running') return
    setRun({ state: 'running' })
    try {
      // Noch einmal holen und lesen: Zwischen Öffnen und Klick können Cloud und Hauptfenster weiter sein.
      const fresh = await loadDevice()
      if (fresh.needsConnection) {
        setState(fresh)
        setRun({ state: 'idle' })
        return
      }
      const finalPlan = planRestore({
        file: selectRows(backup.data.tables, selected, { allTags: selectsEverything(tree, selected) }),
        device: fresh.device,
        foreignAccount,
        accountId: fresh.accountId,
        keepCloudIds: fresh.synced && !foreignAccount,
        cloudRows: fresh.cloudRows,
      })
      await dataSource.writeBackupRows(finalPlan.write)
      // Gleich hochladen statt auf den nächsten Takt des Hauptfensters zu warten.
      if (fresh.synced) void bridge.syncInMain().catch(() => {})
      setRun({
        state: 'done',
        revived: summarize(finalPlan.outcome, o => o.revived.length),
        added: summarize(finalPlan.outcome, o => o.added.length),
        skipped: sum(summarize(finalPlan.outcome, o => o.skipped)),
      })
    } catch (error) {
      console.error('[Backup] Wiederherstellen fehlgeschlagen:', error)
      setRun({ state: 'failed' })
    }
  }

  const title = t('preferences.backups.restoreTitle')
  const pageTitle = t('settings.subNav.backups')
  const done = run.state === 'done'

  return (
    <SettingsSubpage pageTitle={pageTitle} title={title} onBack={onBack} locked={run.state === 'running'}>
      <SourceLine manifest={backup.manifest} language={i18n?.language} />
      {foreignAccount && (
        <p className="backup-views__result">
          <Info size={16} aria-hidden="true" />
          {/* Nicht angemeldet: Mit Anmeldung käme es als eigenes zurück, ohne als Kopie (entschieden 11.10.2026). */}
          {t(state?.accountId === null ? 'preferences.backups.restoreView.signInFirst' : 'preferences.backups.restoreView.foreignAccount')}
        </p>
      )}
      {!state ? (
        <p className="backup-views__loading">
          <Loader2 size={16} className="backup-views__spinner" aria-hidden="true" />
          {t('preferences.backups.restoreView.preparing')}
        </p>
      ) : (
        <>
          <p className="backup-views__intro">{t('preferences.backups.restoreView.treeHint')}</p>
          <BackupSelectionTree
            tree={tree}
            value={selected}
            onValueChange={setSelected}
            disabled={run.state === 'running' || done}
            unavailableHint={t('preferences.backups.tree.notInFile')}
          />
          {preview && !done && <RestorePreview preview={preview} />}
          {state.needsConnection && (
            <p className="backup-views__result backup-views__result--failed" role="alert">
              <WifiOff size={16} aria-hidden="true" />
              {t('preferences.backups.restoreView.needsConnection')}
            </p>
          )}
          {!done && (
            <div className="backup-views__actions">
              <Button
                variant="primary"
                disabled={run.state === 'running' || state.needsConnection || !preview || preview.nothingToDo}
                icon={run.state === 'running' ? <Loader2 size={16} className="backup-views__spinner" aria-hidden="true" /> : undefined}
                onClick={() => void onRestore()}
              >
                {t('preferences.backups.restoreView.action')}
              </Button>
              {state.needsConnection && (
                <Button variant="secondary" onClick={() => void loadDevice().then(setState)}>
                  {t('preferences.backups.restoreView.retry')}
                </Button>
              )}
            </div>
          )}
          <div role="status" aria-live="polite">
            {run.state === 'done' && <RestoreResult revived={run.revived} added={run.added} skipped={run.skipped} />}
            {run.state === 'failed' && (
              <p className="backup-views__result backup-views__result--failed">
                <CircleAlert size={16} aria-hidden="true" />
                {t('preferences.backups.restoreView.failed')}
              </p>
            )}
          </div>
        </>
      )}
    </SettingsSubpage>
  )
}

/** Was geschehen ist, nach Art benannt wie in der Vorschau: „Wiederhergestellt: 1 Projekt, 8 Aufgaben“. */
function RestoreResult({ revived, added, skipped }: { revived: Record<SummaryKind, number>; added: Record<SummaryKind, number>; skipped: number }) {
  const { t } = useTranslation()
  const summary = useSummaryText()
  const nothing = sum(revived) + sum(added) === 0
  return (
    <div className="backup-views__results">
      {nothing && (
        <p className="backup-views__result backup-views__result--done">
          <CircleCheck size={16} aria-hidden="true" />
          {t('preferences.backups.restoreView.resultNothing')}
        </p>
      )}
      {sum(revived) > 0 && (
        <p className="backup-views__result backup-views__result--done">
          <CircleCheck size={16} aria-hidden="true" />
          {t('preferences.backups.restoreView.resultRevived', { summary: summary(revived) })}
        </p>
      )}
      {sum(added) > 0 && (
        <p className="backup-views__result backup-views__result--done">
          <CircleCheck size={16} aria-hidden="true" />
          {t('preferences.backups.restoreView.resultAdded', { summary: summary(added) })}
        </p>
      )}
      {skipped > 0 && (
        <p className="backup-views__result">
          <Info size={16} aria-hidden="true" />
          {t('preferences.backups.restoreView.resultSkipped', { count: skipped })}
        </p>
      )}
    </div>
  )
}
